import { useAppDispatch, useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import QuoteRecipientFields from 'components/quote-page/quote-recipient-fields';
import {
  AirHandlerWithQuantityType,
  CondenserWithQuantityType,
  QuoteEmailInputType,
  QuoteEmailResultsType
} from 'data/types';
import { emptyApi } from 'features/api/empty-api';
import { setProjectDetails } from 'features/project';
import {
  setSelectedDuctedSystems,
  setSystemIsQuoted,
  updateSelectedSystem
} from 'features/systems';
import { useQuoteRecipients } from 'hooks/use-quote-recipients';
import { produce } from 'immer';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import axios from 'utils/api';
import { buildQuoteKitDescription } from 'utils/compatibility-systems';
import { KEPT_EQUIPMENT_PRODUCT_ID } from 'utils/equipment-placeholder';
import { abbreviateNumber } from 'utils/helpers';
import { saveQuoteInfo } from 'utils/quote';
import { quoteAccessoryCategory } from 'utils/quote-accessory-categories';
import {
  buildQuoteRecipientInput,
  withRememberedQuoteRecipients
} from 'utils/quote-recipients';
import { filterUnsentQuoteIDs } from 'utils/quote-send-batch';
import { notifyQuoteSendResult } from 'utils/quote-send-notification';
import { resolveQuoteSendStatus } from 'utils/quote-send-status';
import { errorModal } from 'utils/ui';

type SendQuoteModalProps = {
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
};

export default function SendQuoteModal({ openState }: SendQuoteModalProps) {
  const dispatch = useAppDispatch();
  const { projectDetails } = useAppSelector((state) => state.project);
  const { selectedSystems, selectedDuctedSystems } = useAppSelector(
    (state) => state.systems
  );
  const { quoteIDsToSend } = useAppSelector((state) => state.ui);
  const [open, setOpen] = openState;
  const {
    recipients,
    draft: recipientDraft,
    error: recipientError,
    handleEmailChange,
    handlePhoneNumberChange,
    handleEmailInputChange,
    handlePhoneNumberInputChange,
    validate: validateRecipients
  } = useQuoteRecipients(open);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSendQuote = async () => {
    if (submittingRef.current) {
      return;
    }
    if (validateRecipients()) {
      submittingRef.current = true;
      setIsSubmitting(true);
      setOpen(false);
      const uniqueQuoteIDsToSendSnapshot = filterUnsentQuoteIDs(
        quoteIDsToSend,
        selectedSystems,
        selectedDuctedSystems
      );
      selectedSystems.forEach((system) => {
        if (uniqueQuoteIDsToSendSnapshot.includes(system.uniqueId)) {
          dispatch(
            updateSelectedSystem({
              uniqueId: system.uniqueId,
              quoteStatus: 'sending'
            })
          );
        }
      });

      dispatch(
        setSelectedDuctedSystems(
          produce(selectedDuctedSystems, (draft) => {
            draft.forEach((system) => {
              if (uniqueQuoteIDsToSendSnapshot.includes(system.id.toString())) {
                // eslint-disable-next-line no-param-reassign
                system.quoteStatus = 'sending';
              }
            });
          })
        )
      );

      const kits = {};
      let systemIndex = 0;

      selectedDuctedSystems
        .filter((system) =>
          uniqueQuoteIDsToSendSnapshot.includes(system.id.toString())
        )
        .forEach((system) => {
          systemIndex += 1;
          const kitName = `Option ${systemIndex}`;

          kits[kitName] = {
            uniqueId: system.id,
            description: `${abbreviateNumber(
              Math.round((system.heatingBTU ?? 0) / 1000) * 1000
            )} BTU Split System`,
            products: [
              ...system.products.map((p) => ({
                productID: p.productId,
                quantity: p.quantity,
                quoteCategory: 'Primary Equipment'
              })),
              ...system.quotableAccessories.map((acc) => ({
                productID: acc.productId,
                quantity: acc.quantity,
                quoteCategory: quoteAccessoryCategory(acc.type)
              }))
            ]
          };
        });

      selectedSystems
        .filter((system) =>
          uniqueQuoteIDsToSendSnapshot.includes(system.uniqueId)
        )
        .forEach((system) => {
          systemIndex += 1;
          const kitName = `Option ${systemIndex}`;
          const airHandlerProducts: AirHandlerWithQuantityType[] = [];
          const condenserProducts: CondenserWithQuantityType[] = [];

          system.configuration.condensers.forEach((cond) => {
            if (
              condenserProducts.find(
                (p) => p.productId === cond.selectedProduct?.productId
              )
            ) {
              const productIndex = condenserProducts.findIndex(
                (p) => p.productId === cond.selectedProduct?.productId
              );
              condenserProducts[productIndex].quantity += 1;
            } else if (cond.selectedProduct) {
              condenserProducts.push({
                ...cond.selectedProduct,
                quantity: 1
              });
            }

            cond.zones.forEach((zone) => {
              if (
                airHandlerProducts.find(
                  (p) => p.productId === zone.selectedProduct?.productId
                )
              ) {
                const productIndex = airHandlerProducts.findIndex(
                  (p) => p.productId === zone.selectedProduct?.productId
                );
                airHandlerProducts[productIndex].quantity += 1;
              } else if (zone.selectedProduct) {
                airHandlerProducts.push({
                  ...zone.selectedProduct,
                  quantity: 1
                });
              }

              // Kept blower placeholders (sentinel productId 0, AHA-6796)
              // never become a kit line — they aren't purchasable products.
              if (
                zone.blowerProduct &&
                zone.blowerProduct.productId !== KEPT_EQUIPMENT_PRODUCT_ID
              ) {
                const blowerIdx = airHandlerProducts.findIndex(
                  (p) => p.productId === zone.blowerProduct!.productId
                );
                if (blowerIdx >= 0) {
                  airHandlerProducts[blowerIdx].quantity += 1;
                } else {
                  airHandlerProducts.push({
                    ...zone.blowerProduct,
                    quantity: 1
                  });
                }
              }
            });
          });

          // Kit description with the kept-pairing marker carried into the
          // quoted artifact (AHA-6681 sub-build 4).
          const description = buildQuoteKitDescription(system);

          kits[kitName] = {
            uniqueId: system.uniqueId,
            description,
            products: [
              ...airHandlerProducts.map((p) => ({
                productID: p.productId,
                quantity: p.quantity,
                quoteCategory: 'Primary Equipment'
              })),
              ...condenserProducts.map((p) => ({
                productID: p.productId,
                quantity: p.quantity,
                quoteCategory: 'Primary Equipment'
              })),
              ...system.quotableAccessories.map((acc) => ({
                productID: acc.productId,
                quantity: acc.quantity,
                quoteCategory: quoteAccessoryCategory(acc.type)
              }))
            ]
          };
        });

      const quoteInput: QuoteEmailInputType = {
        ...buildQuoteRecipientInput(recipients),
        kbType: 'Mini-Split v2',
        projectId: projectDetails?.projectId,
        customerContactId: projectDetails?.customerContactId,
        customerName: projectDetails?.customerName,
        kits
      };

      axios
        .post<QuoteEmailResultsType>(
          `${process.env.REACT_APP_PARTNERNET_URL}/kitbuilder/emailresults`,
          quoteInput
        )
        .then(async (res) => {
          const quoteStatus = resolveQuoteSendStatus(res.data, recipients);
          await saveQuoteInfo(
            res.data,
            uniqueQuoteIDsToSendSnapshot,
            quoteStatus
          );

          dispatch(
            emptyApi.util.invalidateTags(['QuoteData', 'QuotableSystems'])
          );

          notifyQuoteSendResult(res.data);

          selectedSystems.forEach((system) => {
            if (uniqueQuoteIDsToSendSnapshot.includes(system.uniqueId)) {
              dispatch(
                updateSelectedSystem({
                  uniqueId: system.uniqueId,
                  quoteStatus
                })
              );
              dispatch(
                setSystemIsQuoted({
                  uniqueId: system.uniqueId,
                  isQuoted: quoteStatus === 'sent'
                })
              );
            }
          });

          dispatch(
            setSelectedDuctedSystems(
              produce(selectedDuctedSystems, (draft) => {
                draft.forEach((system) => {
                  if (
                    uniqueQuoteIDsToSendSnapshot.includes(system.id.toString())
                  ) {
                    // eslint-disable-next-line no-param-reassign
                    system.quoteStatus = quoteStatus;
                  }
                });
              })
            )
          );

          dispatch(
            setProjectDetails(
              withRememberedQuoteRecipients(projectDetails, recipients)
            )
          );
        })
        .catch((error) => {
          console.error(error);
          errorModal('Failed to send quote');
          submittingRef.current = false;
          setIsSubmitting(false);

          selectedSystems.forEach((system) => {
            if (uniqueQuoteIDsToSendSnapshot.includes(system.uniqueId)) {
              dispatch(
                updateSelectedSystem({
                  uniqueId: system.uniqueId,
                  quoteStatus: 'unsent'
                })
              );
            }
          });

          dispatch(
            setSelectedDuctedSystems(
              produce(selectedDuctedSystems, (draft) => {
                draft.forEach((system) => {
                  if (
                    uniqueQuoteIDsToSendSnapshot.includes(system.id.toString())
                  ) {
                    // eslint-disable-next-line no-param-reassign
                    system.quoteStatus = 'unsent';
                  }
                });
              })
            )
          );
        });
    }
  };

  return (
    <Modal
      title='Send Quote to Customer'
      buttonText={isSubmitting ? 'Sending...' : 'Send Quote'}
      openState={openState}
      onSave={handleSendQuote}
      saveDisabled={isSubmitting}
      closeOnSave={false}
    >
      <QuoteRecipientFields
        emailOptions={projectDetails?.customerEmails ?? []}
        phoneNumberOptions={projectDetails?.customerPhoneNumbers ?? []}
        draft={recipientDraft}
        error={recipientError}
        onEmailChange={handleEmailChange}
        onPhoneNumberChange={handlePhoneNumberChange}
        onEmailInputChange={handleEmailInputChange}
        onPhoneNumberInputChange={handlePhoneNumberInputChange}
      />
    </Modal>
  );
}
